import { firebaseAuth } from "./firebase.js";

export const API_BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiRequestError extends Error {
  constructor(message: string, public readonly status: number) { super(message); this.name = "ApiRequestError"; }
}

async function withAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(new DOMException("La solicitud fue cancelada.", "AbortError"));
    void promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
    if (signal.aborted) { abort(); return; }
    signal.addEventListener("abort", abort, { once: true });
  });
}

export async function getAuthHeaders(signal?: AbortSignal): Promise<Record<string, string>> {
  const currentUser = firebaseAuth?.currentUser;
  if (currentUser === null || currentUser === undefined) return {};
  const token = currentUser.getIdToken();
  return { Authorization: `Bearer ${await (signal ? withAbort(token, signal) : token)}` };
}

export async function requestJson<T>(path: string, init: RequestInit = {}, authenticated = true): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (init.signal?.aborted) controller.abort();
  init.signal?.addEventListener("abort", abort, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    signal: controller.signal,
    headers: {
      Accept: "application/json",
      ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(authenticated ? await getAuthHeaders(controller.signal) : {}),
      ...(init.headers ?? {})
    }
    });
    if (!response.ok) {
      let detail = `La API devolvió ${response.status}.`;
      try {
        const problem = await response.json() as { detail?: unknown };
        if (typeof problem.detail === "string") detail = problem.detail;
      } catch {
        // Conserva el mensaje genérico si la respuesta no es JSON.
      }
      throw new ApiRequestError(detail, response.status);
    }
    return await response.json() as T;
  } catch (failure) {
    if (controller.signal.aborted && !init.signal?.aborted) throw new Error("La conexión tardó demasiado. Revisá tu conexión e intentá de nuevo.");
    throw failure;
  } finally {
    window.clearTimeout(timeout);
    init.signal?.removeEventListener("abort", abort);
  }
}

export function getPublicJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  return requestJson<T>(path, signal ? { signal } : {}, false);
}

export function getJson<T>(path: string): Promise<T> {
  return requestJson<T>(path);
}

export function postJson<T>(path: string, body: unknown): Promise<T> {
  return requestJson<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function putJson<T>(path: string, body: unknown): Promise<T> {
  return requestJson<T>(path, { method: "PUT", body: JSON.stringify(body) });
}

export function deleteJson<T>(path: string): Promise<T> {
  return requestJson<T>(path, { method: "DELETE" });
}
